import type { ClientAgreementService, ClientDocumentsDto, DecisionListItem } from '@fos/shared/contracts/knowledge';
import type { BillingCycle, ServiceKind } from '@fos/shared/contracts/services';
import { BILLING_CYCLE_SUFFIX, SERVICE_KIND_LABELS } from '@fos/shared/labels';
import { todayLocal } from '@fos/shared/time';
import { Gavel, Handshake, Plus } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../../lib/api';
import { formatDate, formatDateShort, formatDuration, formatMoney } from '../../../../lib/format';
import { trpc } from '../../../../lib/trpc';
import { Button } from '../../../../ui/Button';
import { DateInput } from '../../../../ui/DateTimeInput';
import { Dialog } from '../../../../ui/Dialog';
import { Field } from '../../../../ui/Field';
import { Input, Textarea } from '../../../../ui/Input';
import { Mono } from '../../../../ui/Mono';
import { SectionHeader } from '../../../../ui/PageHeader';
import { Section } from '../../../../ui/Section';
import { EmptyState } from '../../../../ui/States';
import { StatusPill } from '../../../../ui/StatusPill';
import { toast } from '../../../../ui/Toast';
import { FULLSCREEN_ON_PHONE, TOUCH_BUTTON, TOUCH_TEXT } from '../../../mobile';

/** „od 1.01.2026 do 31.12.2026”, „od 1.01.2026”, null. */
function periodLabel(start: string | null, end: string | null): string | null {
  if (start && end) return `${formatDate(start)} – ${formatDate(end)}`;
  if (start) return `od ${formatDate(start)}`;
  if (end) return `do ${formatDate(end)}`;
  return null;
}

function ServiceRow({ service }: { service: ClientAgreementService }) {
  const kind = SERVICE_KIND_LABELS[service.kind as ServiceKind] ?? service.kind;
  const suffix = BILLING_CYCLE_SUFFIX[service.cycle as BillingCycle] ?? '';
  const meta = [
    periodLabel(service.periodStart, service.periodEnd),
    service.hoursPoolSeconds ? `pula ${formatDuration(service.hoursPoolSeconds)} / mies.` : null,
    service.price ? `${formatMoney(service.price, service.currency)}${suffix ? ` ${suffix}` : ''}` : null,
  ].filter(Boolean);
  return (
    <li className="flex flex-col gap-1 border-b border-line py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-body-s font-medium text-fg">{service.name}</span>
        <span className="text-caption text-fg-muted">{kind}</span>
        {service.status === 'paused' && (
          <StatusPill size="s" tone="waiting">
            Wstrzymana
          </StatusPill>
        )}
      </div>
      {meta.length > 0 && <span className="text-caption text-fg-secondary">{meta.join(' · ')}</span>}
      {service.descriptionMd && (
        <p className="line-clamp-3 text-caption whitespace-pre-line text-fg-secondary">{service.descriptionMd}</p>
      )}
      {service.billingTerms && (
        <p className="text-caption text-fg-secondary">
          <span className="text-fg-muted">Warunki: </span>
          {service.billingTerms}
        </p>
      )}
    </li>
  );
}

function DecisionRow({ decision }: { decision: DecisionListItem }) {
  return (
    <li className="flex gap-4 border-b border-line py-3">
      <Mono className="w-11 shrink-0 pt-0.5 text-caption text-fg-muted">
        {decision.decidedOn ? formatDateShort(decision.decidedOn) : '—'}
      </Mono>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <Link
          to={`/wiedza/${decision.id}`}
          className={`rounded-xs text-body-s text-fg hover:underline focus-visible:outline-2 focus-visible:outline-accent ${TOUCH_TEXT}`}
        >
          {decision.title}
        </Link>
        {decision.excerpt && <p className="line-clamp-2 text-caption text-fg-muted">{decision.excerpt}</p>}
        {decision.project && <span className="text-caption text-fg-muted">{decision.project.name}</span>}
      </div>
    </li>
  );
}

/** „Dodaj ustalenie” = notatka-decyzja Wiedzy przypięta do klienta (tytuł, opcjonalna treść, data — domyślnie dziś). */
function AddDecisionDialog({
  clientId,
  open,
  onOpenChange,
}: {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [decidedOn, setDecidedOn] = useState(() => todayLocal());
  const [titleError, setTitleError] = useState<string | null>(null);
  const create = trpc.knowledge.create.useMutation({
    onSuccess: async () => {
      toast.success('Ustalenie zapisane', { description: 'Jest też w Wiedzy klienta jako decyzja.' });
      onOpenChange(false);
      setTitle('');
      setBody('');
      setDecidedOn(todayLocal());
      await Promise.all([
        utils.clients.documents.list.invalidate({ clientId }),
        utils.knowledge.listDecisions.invalidate(),
      ]);
    },
    onError: (err) => toast.error('Nie udało się zapisać ustalenia', { description: errorMessage(err) }),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError('Napisz, co ustaliliście');
      return;
    }
    create.mutate({
      scope: 'client',
      clientId,
      kind: 'decision',
      title: title.trim(),
      bodyMd: body.trim(),
      decidedOn: decidedOn || null,
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Dodaj ustalenie"
      description="Krótko: co ustaliliście z klientem. Trafi do Wiedzy klienta jako decyzja."
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className={TOUCH_BUTTON}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="add-decision-form"
            loading={create.isPending}
            className={TOUCH_BUTTON}
          >
            Zapisz ustalenie
          </Button>
        </>
      }
    >
      <form id="add-decision-form" onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Ustalenie" error={titleError}>
          <Input
            value={title}
            maxLength={300}
            placeholder="np. Poprawki do 2 dni roboczych, w ramach puli"
            onChange={(e) => {
              setTitle(e.target.value);
              setTitleError(null);
            }}
          />
        </Field>
        <Field label="Szczegóły" hint="Opcjonalnie: kto, kiedy, na jakich warunkach.">
          <Textarea value={body} rows={4} onChange={(e) => setBody(e.target.value)} />
        </Field>
        <Field label="Data ustalenia">
          <DateInput value={decidedOn} onValueChange={setDecidedOn} />
        </Field>
      </form>
    </Dialog>
  );
}

/**
 * „Ustalenia” w dokumentach klienta: aktywne usługi z warunkami i pulą godzin (cena tylko z „Widzi stawki i kwoty” —
 * backend pomija ją i ukrywa kwoty w tekście) oraz decyzje klienta z Wiedzy; „Dodaj ustalenie”.
 */
export function AgreementsSection({
  clientId,
  agreements,
}: {
  clientId: string;
  agreements: ClientDocumentsDto['agreements'];
}) {
  const [open, setOpen] = useState(false);
  const services = agreements.services ?? [];
  const decisions = agreements.decisions ?? [];
  const empty = services.length === 0 && decisions.length === 0;
  const add = agreements.canAddDecision ? (
    <Button variant="ghost" size="s" icon={Plus} onClick={() => setOpen(true)} className={TOUCH_BUTTON}>
      Dodaj ustalenie
    </Button>
  ) : undefined;

  return (
    <Section aria-labelledby="client-documents-agreements">
      <SectionHeader title={<span id="client-documents-agreements">Ustalenia</span>} action={empty ? undefined : add} />
      {empty ? (
        <EmptyState
          compact
          icon={Handshake}
          title="Brak ustaleń"
          description="Zapisz warunek albo decyzję, żeby nie zginęła w mailach."
          action={add}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {services.length > 0 && (
            <div>
              <h3 className="text-caption font-medium text-fg-muted">Usługi</h3>
              <ul aria-label="Usługi klienta">
                {services.map((s) => (
                  <ServiceRow key={s.id} service={s} />
                ))}
              </ul>
            </div>
          )}
          {decisions.length > 0 && (
            <div>
              <h3 className="flex items-center gap-1.5 text-caption font-medium text-fg-muted">
                <Gavel size={12} strokeWidth={2} aria-hidden />
                Decyzje
              </h3>
              <ul aria-label="Decyzje klienta">
                {decisions.map((d) => (
                  <DecisionRow key={d.id} decision={d} />
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {agreements.canAddDecision && <AddDecisionDialog clientId={clientId} open={open} onOpenChange={setOpen} />}
    </Section>
  );
}
